/**
 * Preferencias de correo de cada persona (CDT-219, ADR-0019 y plan de correos).
 *
 * Hoy solo los avisos: cuatro interruptores, todos encendidos por defecto. Cada persona (cualquier rol, incluida
 * LECTURA) cambia los suyos; solo un ADMIN cambia los de otra persona. Sin fila guardada rigen
 * `PREFERENCIAS_POR_DEFECTO`.
 *
 * Diseño pensado para crecer: el reporte periódico (PR 6) se suma como otra clave (`reporte`) junto a `avisos` en la
 * entrada y en la salida, sin tocar lo que ya existe.
 */
import { z } from 'zod';
import { fechaIsoSchema } from './comunes';

/** Tipos de aviso por correo que una persona puede apagar. */
export const TIPOS_AVISO = ['COMENTARIO', 'ASIGNADA', 'ESTADO', 'FECHAS'] as const;
export type TipoAviso = (typeof TIPOS_AVISO)[number];

export const ETIQUETA_TIPO_AVISO: Record<TipoAviso, string> = {
  COMENTARIO: 'Comentarios en mis tareas',
  ASIGNADA: 'Me asignan una tarea',
  ESTADO: 'Cambios de estado',
  FECHAS: 'Fechas límite',
};

export const EMOJI_TIPO_AVISO: Record<TipoAviso, string> = {
  COMENTARIO: '💬',
  ASIGNADA: '👤',
  ESTADO: '✅',
  FECHAS: '⏰',
};

/**
 * Un booleano por tipo de aviso (`true` = se envía). `.strict()`: un tipo desconocido es un error, no se ignora en
 * silencio. Sin `.default()`: el PUT manda siempre los cuatro (Zod 4 no permite `.partial()` sobre defaults).
 */
export const avisosCorreoSchema = z
  .object({
    COMENTARIO: z.boolean(),
    ASIGNADA: z.boolean(),
    ESTADO: z.boolean(),
    FECHAS: z.boolean(),
  })
  .strict();
export type AvisosCorreo = z.infer<typeof avisosCorreoSchema>;

/** Entrada de `PUT /api/cuenta/correos` y `PUT /api/usuarios/:id/correos`: reemplaza todo (PUT completo). */
export const guardarPreferenciasCorreoSchema = z
  .object({
    avisos: avisosCorreoSchema,
  })
  .strict();
export type GuardarPreferenciasCorreoEntrada = z.infer<typeof guardarPreferenciasCorreoSchema>;

/** Respuesta de los `GET` y `PUT` de preferencias de correo. */
export const preferenciasCorreoSchema = z.object({
  avisos: avisosCorreoSchema,
  /** `true` si el último cambio lo hizo otra persona (un ADMIN desde Personas). */
  modificadoPorAdmin: z.boolean(),
  /** Último cambio; `null` si la persona nunca las guardó (rigen las de por defecto). */
  actualizadoEn: fechaIsoSchema.nullable(),
});
export type PreferenciasCorreo = z.infer<typeof preferenciasCorreoSchema>;

/**
 * Todo encendido: lo que rige para quien nunca guardó sus preferencias. Solo lectura en profundidad (`as const` para
 * el compilador y `Object.freeze` en ejecución): para modificarlas, usa la copia de `preferenciasCorreoPorDefecto()`.
 */
export const PREFERENCIAS_POR_DEFECTO = Object.freeze({
  avisos: Object.freeze({ COMENTARIO: true, ASIGNADA: true, ESTADO: true, FECHAS: true } as const),
  modificadoPorAdmin: false,
  actualizadoEn: null,
} as const) satisfies PreferenciasCorreo;

/** Copia de las preferencias por defecto (para no compartir ni mutar el objeto congelado). */
export const preferenciasCorreoPorDefecto = (): PreferenciasCorreo => ({
  avisos: { ...PREFERENCIAS_POR_DEFECTO.avisos },
  modificadoPorAdmin: false,
  actualizadoEn: null,
});
